import React, { SyntheticEvent, useEffect, useState } from "react"
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from "@mui/material"
import { Row } from "@components/VerticalTable"
import PropertiesTable, { NodeProperties } from "@features/graph/details/components/PropertiesTable"
import { deepCopy } from "@features/graph/utils/graphUtils"
import { graphReplaceNode } from "@features/graph/state/graphReplaceNode"
import { findNode } from "@utils/graph"
import { GraphStoreType } from "@features/graph/Graph"
import { Node, useStoreApi } from "reactflow"
import { GraphDataViewModel, GraphEditMode } from "@features/graph/graphViewModel"
import { EditType } from "@components/EditableCell"
import { ParentChildDetails } from "@features/graph/details/components/ParentChildDetails/ParentChildDetails"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useLocalStorage } from "usehooks-ts"
import { NodeRowsAccordion } from "@features/graph/details/components/NodeRowsAccordion"
import { getNodeTypeMap } from "@features/graph/utils/graphNodeUtils"
import { NodeDetailsLabel } from "@features/graph/details/components/NodeDetailsLabel"
import { GraphNodeType } from "@custom-types/graphs"
import NodeTagsAccordion from "@features/graph/details/components/NodeTagsAccordion"

type ContiniousNodeDetailsProps = {
  nodeId: string
  editMode: GraphEditMode
  graphKey?: string
  nodeType: GraphNodeType.CONTINOUS | GraphNodeType.MIXTURE
}

export const ContinuousNodeDetails = ({
  nodeId,
  editMode,
  graphKey,
  nodeType,
}: ContiniousNodeDetailsProps) => {
  const graphStore: GraphStoreType = useStoreApi()
  const [node, setNode] = useState<Node>()
  const [expandedPropertiesTable, setExpandedPropertiesTable] = useLocalStorage(
    `nodePropertiesTableAccordionExpanded`,
    true,
  )

  useEffect(() => {
    setNode(findNode(graphStore, nodeId))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [nodeId])

  if (!node) {
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  const properties: NodeProperties<any> = node.data.node?.properties ?? {}
  const sampleValue = properties.a_sample_value
  const nodeTypeMap = getNodeTypeMap(GraphDataViewModel.data?.nodeMap ?? {})

  const { label, color } = node.data?.display ?? {}

  const nodeRows: Row[] = [
    { label: "Type", value: nodeType === GraphNodeType.CONTINOUS ? "Continuous" : "Mixture" },
    { label: "Historic Mnemonic", value: node.data?.display["Historic Mnem"] },
    { label: "Unit", value: formatValuePythonic("", node.data?.display["Unit Format"]) || "-" },
    { label: "Description", value: node.data?.display["Description"] || "-" },
    { label: "Exclude From Export", value: node.data?.display.exportExcluded ? "yes" : "no" },
    { label: "Link", value: node.data.display.Link ?? "" },
    // { label: "Color", children: <ColorPreview color={color} />, value: color },
  ]

  if (sampleValue !== null) {
    nodeRows.push({
      label: "Sample value",
      value: sampleValue,
      editable: editMode === GraphEditMode.SCENARIO,
      type: EditType.Number,
      onBlur: (newValue) => handlePropertiesFieldCellBlur("a_sample_value", newValue),
    })
  }

  const handlePropertiesFieldCellBlur = async (field: string, newValue: string) => {
    if (!graphKey) {
      return
    }

    node.data.node.properties[field] = Number(newValue)
    await graphReplaceNode(graphStore, graphKey, [node])

    setNode(deepCopy(node))
  }

  const handleDfCpdFieldCellBlur = async (field: string, newValue: string, index: number) => {
    if (!graphKey) {
      return
    }

    const isOptionValue = node.data.node.properties.parents_lst.includes(newValue)
    const parsedValue = isOptionValue ? newValue : Number(newValue)

    node.data.node.properties.df_cpd[field][index] = parsedValue
    await graphReplaceNode(graphStore, graphKey, [node])

    setNode(deepCopy(node))
  }

  return (
    <Grid container direction="column" sx={{ p: 1 }} spacing={1}>
      <Grid item>
        <NodeDetailsLabel label={label} />
      </Grid>
      <NodeRowsAccordion nodeRows={nodeRows} />
      <NodeTagsAccordion tags={node.data.display.tags ?? []} />
      <Grid item sx={{ width: expandedPropertiesTable ? "auto" : "100%" }}>
        <Accordion
          expanded={expandedPropertiesTable}
          onChange={(event: SyntheticEvent, isExpanded: boolean) => {
            setExpandedPropertiesTable(isExpanded)
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>Properties Table</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <PropertiesTable
              type={node.data.node.type}
              editable={editMode === GraphEditMode.SCENARIO}
              labelMap={node.data.labelMap}
              nodeTypeMap={nodeTypeMap}
              properties={properties}
              onBlur={handleDfCpdFieldCellBlur}
            />
          </AccordionDetails>
        </Accordion>
      </Grid>
      <ParentChildDetails />
    </Grid>
  )
}
